import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../services/apiClient';
import type { RecommendationEntry, RecommendationSubject } from './useRecommendations';

/**
 * 目的軸モード (計画書§6・P3-C)。
 * GET /generation/purpose-suggestions の purpose 値域。
 * バックエンド purpose_service.VALID_PURPOSES と同期。
 */
export type PurposeKind = 'banner' | 'product_shot' | 'scene' | 'sns';

export type BannerTemplateSuggestion = {
  option_key: string;
  label: string;
  category_code: number | null;
  category_label: string;
  image_url: string | null;
  score: number;
  category_match: boolean;
};

export type BannerPurposeSuggestions = {
  status: 'ready';
  purpose: 'banner';
  aspect_ratio: string;
  /** ai_subject_profiles の状態。バッジ表示等はせずランキングの参考にのみ使う (計画書指示)。 */
  profile_status: 'ready' | 'pending' | 'failed' | 'none' | 'unsupported';
  banner: {
    /** バナーテンプレのトップ group key (通常 'banner_ad')。selectTemplateOption に渡す。 */
    group_key: string;
    templates: BannerTemplateSuggestion[];
  };
};

export type PresetPurposeSuggestions = {
  status: 'ready';
  purpose: 'product_shot' | 'scene' | 'sns';
  aspect_ratio: string;
  person_included: boolean;
  /** 直近group key -> 選択option key。settings.attributes へそのまま setAttribute する。 */
  preset_selections: Record<string, string>;
  recommended: {
    strong: RecommendationEntry[];
    suggested: RecommendationEntry[];
  };
};

/** migration未適用 (banner) / 想定外エラー (全 purpose 共通のフォールバック) 時。 */
export type UnavailablePurposeSuggestions = {
  status: 'unavailable';
  purpose: PurposeKind;
};

export type PurposeSuggestionsResponse =
  | BannerPurposeSuggestions
  | PresetPurposeSuggestions
  | UnavailablePurposeSuggestions;

/**
 * 目的軸モードのサジェストを取得するフック。
 *
 * subject (選択素材) は任意: 未選択でも purpose だけで叩ける (バックエンドが
 * profile なし前提で degrade する)。purpose が null の間は無効化。
 */
export function usePurposeSuggestions(
  purpose: PurposeKind | null,
  subject: RecommendationSubject | null,
) {
  return useQuery<PurposeSuggestionsResponse>({
    queryKey: ['generation', 'purpose-suggestions', purpose, subject?.subject_type, subject?.subject_id],
    queryFn: async () => {
      const { data } = await apiClient.get<PurposeSuggestionsResponse>('/generation/purpose-suggestions', {
        params: {
          purpose,
          ...(subject ? { subject_type: subject.subject_type, subject_id: subject.subject_id } : {}),
        },
      });
      return data;
    },
    enabled: !!purpose,
    staleTime: 5 * 60_000,
  });
}
